import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { adminApi } from "../api/adminApi";
import type { SetupStatus } from "../api/types";

interface DeploymentValue {
  /** null until the first response arrives. */
  status: SetupStatus | null;
  loading: boolean;
  isOnPrem: boolean;
  /** On-prem with no valid license: everything but sign-in and the license page is refused. */
  blocked: boolean;
  refresh: () => Promise<void>;
}

const DeploymentContext = createContext<DeploymentValue | undefined>(undefined);

/** Knows whether this is the hosted SaaS or a licensed on-premise install, and (on-prem) the
 *  license state. Refreshes on window focus and whenever the API refuses a call because of the
 *  license, so an expiry is noticed without a manual reload. */
export function DeploymentProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SetupStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setStatus(await adminApi.setup.status());
    } catch {
      /* keep the last known status - a blip must not flip the app into a different mode */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    window.addEventListener("reportforge:license-blocked", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener("reportforge:license-blocked", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [refresh]);

  const value = useMemo<DeploymentValue>(() => {
    const isOnPrem = status?.mode === "ONPREM";
    return {
      status,
      loading,
      isOnPrem,
      blocked: isOnPrem && (status?.status === "EXPIRED" || status?.status === "UNLICENSED"),
      refresh,
    };
  }, [status, loading, refresh]);

  return <DeploymentContext.Provider value={value}>{children}</DeploymentContext.Provider>;
}

export function useDeployment(): DeploymentValue {
  const ctx = useContext(DeploymentContext);
  if (!ctx) throw new Error("useDeployment must be used within DeploymentProvider");
  return ctx;
}
